import {
  PropertyCountry,
  PropertyHome,
  PropertyLocation,
  PropertyBooking,
  AccountIconCMS,
} from 'common/components/svgs';
import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import queryString from 'query-string';
import cookies from 'js-cookie';
import { Link } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import { Input, AutoComplete, message, Select } from 'antd';
import { isNumber, isNaN } from 'lodash';
import { Base64 } from 'js-base64';
import { getSafeHtml } from 'common/helpers/safeHtml';
import endpoints from '~settings/endpoints';
import * as queries from '~settings/queries';
import generatePath from '~settings/routing';
import { cookieNames } from '~constants';
import { bookingJourneyType } from 'common/constants/booking';
import { showPassportName } from '~helpers';
import { getNameBySlug } from '~helpers/property-edit';
import { extractSearchParams } from '~helpers/history';
import { getItem } from '~helpers/storage';
import { isLandlordRole } from '~helpers/auth';

export default class GlobalSearch extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      source: [],
      value: '',
      searchType: this.handleSearchPathByPathname(props.pathname) ?? 'propertySlug',
      lastSearch: { name: '', slug: '' },
    };
    this.timeout = null;
    this.searchPlaceholder = {
      propertySlug: props.t('cms.search_input.placeholder.property'),
      landlordId: props.t('cms.search_input.placeholder.landlord'),
      bookingId: props.t('cms.search_input.placeholder.booking'),
      citySlugAndCountrySlug: props.t('cms.search_input.placeholder.city_country'),
    };
  }

  componentDidMount() {
    const searchParams = queryString.parse(this.props.search).search;
    const { type, slug } = extractSearchParams(searchParams);
    this.fillNameBySlug(type, slug);
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.pathname !== this.props.pathname &&
      nextProps.pathname !== generatePath('properties', {})
    ) {
      this.setState({
        source: [],
        value: '',
        lastSearch: { name: '', slug: '' },
      });
    }
    if (nextProps.search !== this.props.search) {
      const searchParams = queryString.parse(nextProps.search).search;
      const { type, slug } = extractSearchParams(searchParams);
      if (type && slug) {
        if (slug !== this.state.lastSearch.slug) {
          if (slug) {
            this.fillNameBySlug(type, slug);
          } else {
            this.setState({
              value: '',
              lastSearch: { name: '', slug: '' },
            });
          }
        }
      }
    }
  }

  handleSearchPathByPathname = targetPathname => {
    const pathnameArr = targetPathname.split('/');
    let defaultSearchType = 'propertySlug';
    if (pathnameArr.includes('landlords') || pathnameArr.includes('landlord')) {
      defaultSearchType = 'landlordId';
    } else if (pathnameArr.includes('booking')) {
      defaultSearchType = 'bookingId';
    }
    return defaultSearchType;
  };

  fillNameBySlug = (type, slug) => {
    let query = null;
    switch (type) {
      case 'countrySlug':
        query = queries.queryCountry;
        break;
      case 'citySlug':
        query = queries.queryCity;
        break;
      case 'landlordSlug':
        query = queries.queryLandlord;
        break;
      default:
        query = null;
    }

    if (query && slug) {
      getNameBySlug(query({ slug })).then(name => {
        this.setState({ value: name, lastSearch: { name, slug } });
      });
    }
  };

  boldKeyword = (text, keyword) =>
    String(text)?.replace(
      new RegExp(keyword.replace(/[\\|\[|\?|\*|(|)|+]/gi, ''), 'i'),
      '<span style="color: #38B2A6">$&</span>',
    );

  getPropertyId = id => {
    const source = Base64.decode(id);
    const target = JSON.parse(source) ?? {};

    return (
      this.props.t('cms.header.property_id.text') +
      this.boldKeyword(target.id, this.state.value.trim())
    );
  };

  getEmailAddress = address => {
    if (!address) {
      return '';
    }
    return `(${this.boldKeyword(address, this.state.value.trim())})`;
  };

  getChildren = () => {
    if (this.state.source === null) {
      return [
        <AutoComplete.Option key="noResult">
          <span className="autocomplete__primary-text">
            {this.props.t('cms.message.auto_complete.no_result')}
          </span>
        </AutoComplete.Option>,
      ];
    }

    const typeIcon = {
      property: <PropertyHome className="autocomplete__group-icon" />,
      city: <PropertyLocation className="autocomplete__group-icon" />,
      country: <PropertyCountry className="autocomplete__group-icon" />,
      landlord: <AccountIconCMS className="autocomplete__group-icon" />,
      unionbooking: <PropertyBooking className="autocomplete__group-icon" />,
    };

    const result = [];
    this.state.source.forEach(group => {
      if (group.children && group.children.length > 0) {
        result.push(
          <AutoComplete.OptGroup
            key={group.title}
            label={
              <>
                {typeIcon[group.type]}
                <span>{group.title}</span>
              </>
            }
          >
            <For each="item" of={group.children}>
              <AutoComplete.Option key={item.slug} value={item.slug}>
                <Link to={this.path(item)} className="autocomplete__link">
                  <div className="autocomplete__primary-text-container">
                    <p>
                      <span className="autocomplete__primary-text">
                        {getSafeHtml(this.boldKeyword(item.title, this.state.value.trim()))}
                      </span>
                      <If condition={group.type === 'property'}>
                        <span className="autocomplete__property-id">
                          {getSafeHtml(this.getPropertyId(item.id))}
                        </span>
                      </If>
                      <If condition={group.type === 'unionbooking'}>
                        <span className="autocomplete__booking-email">
                          {getSafeHtml(this.getEmailAddress(item.email))}
                        </span>
                      </If>
                    </p>
                    <If condition={item.subTitle}>
                      <p className="autocomplete__secondary-text">{item.subTitle}</p>
                    </If>
                  </div>
                </Link>
              </AutoComplete.Option>
            </For>
          </AutoComplete.OptGroup>,
        );
      }
      return true;
    });
    return result;
  };

  handleSearch = value => {
    clearTimeout(this.timeout);

    this.timeout = setTimeout(() => {
      this.fetch(value ? value.trim() : '');
    }, 300);
  };

  handleSelect = (value, option) => {
    if (value !== 'noResult') {
      this.setState({ value, lastSearch: { name: value, slug: option.key } });
    }
  };

  handleChange = (value, e) => {
    if (typeof e.props.children === 'string') {
      this.setState({ value });
    }
  };

  fetch = query => {
    const headers = {
      'Accept-language': 'en-us',
      Authorization: cookies.get(cookieNames.token)
        ? `Bearer ${cookies.get(cookieNames.token)}`
        : '',
    };
    const authPayload = getItem('PMS_CURRENT_USER_AUTH');
    if (authPayload && authPayload.payload && authPayload.payload.currentRoleSlug) {
      headers['Current-Role'] = authPayload.payload.currentRoleSlug;
    }

    let originId;

    if (isNumber(Number(query)) && !isNaN(Number(query))) {
      originId = Number(query);
    }

    axios({
      method: 'post',
      url: endpoints.search.url(),
      data: queries.searchByType({ query, originId, pageSize: 20 }, this.state.searchType),
      headers,
    })
      .then(response => {
        if (!response.data.errors) {
          this.processResult(response.data.data.search.edges);
        } else {
          // TODO add more detail error message
          message.error(this.props.t('cms.message.error'));
        }
      })
      .catch(err => {
        console.error(err);
        // TODO add more detail error message
        message.error(this.props.t('cms.message.error'));
      });
  };

  processResult = results => {
    if (!results || results.length === 0) {
      this.setState(Object.assign({}, this.state, { source: null }));
      return false;
    }

    const data = {
      property: [],
      city: [],
      country: [],
      landlord: [],
      unionbooking: [],
    };

    results.forEach(item => {
      let subTitle = null;
      if (item.node.city) {
        subTitle = `${item.node.city.name}, ${item.node.city.country.name}`;
      } else if (item.node.country) {
        subTitle = item.node.country.name;
      }

      if (item.node.type.toLowerCase() === 'unionbooking') {
        data?.['unionbooking'].push({
          type: item.node.type,
          title: showPassportName(item.node.student),
          email: item.node.student.emailAddress,
          subTitle: this.props.t('cms.header.booking_id.text') + item.node.referenceId,
          slug: item.node.referenceId,
          bookingJourney: item.node.bookingJourney,
        });
      } else if (
        !(
          isLandlordRole() &&
          item.node.type.toLowerCase() === 'property' &&
          item.node.status === 'NEW'
        )
      ) {
        data[item.node.type.toLowerCase()].push({
          title: item.node.name,
          subTitle,
          slug: item.node.slug,
          id: item.node.id,
          type: item.node.type,
        });
      }

      return true;
    });

    this.setState(
      Object.assign({}, this.state, {
        source: [
          {
            title: this.props.t('cms.search.title.property'),
            type: 'property',
            children: data.property,
          },
          {
            title: this.props.t('cms.search.title.country'),
            type: 'country',
            children: data.country,
          },
          {
            title: this.props.t('cms.search.title.city'),
            type: 'city',
            children: data.city,
          },
          {
            title: this.props.t('cms.search.title.landlord'),
            type: 'landlord',
            children: data.landlord,
          },
          {
            title: this.props.t('cms.search.title.booking'),
            type: 'unionbooking',
            children: data.unionbooking,
          },
        ],
      }),
    );

    return true;
  };

  path = ({ type, slug, bookingJourney, id }) => {
    const search = `${`${type?.toLowerCase()}Slug`}_${slug}`;

    switch (type) {
      case 'Landlord':
        return generatePath('landlord', {
          landlordSlug: slug,
        });
      case 'Country':
      case 'City':
        return generatePath('properties', {}, { search });
      case 'Property':
        return generatePath('property.homepage', { propertySlug: slug }, { propertyId: id });
      case 'UnionBooking':
        if (bookingJourney === bookingJourneyType.MANUAL) {
          return generatePath('booking.manualDetail', { id: slug });
        }
        if (bookingJourney === bookingJourneyType.SEMI_AUTOMATIC) {
          return generatePath('booking.detail', { id: slug });
        } else {
          return generatePath('booking.bookingDetail', { id: slug, bookingJourney });
        }
      default:
        return false;
    }
  };

  clearFilter = e => {
    e.stopPropagation();
    this.setState({ source: [], value: '', lastSearch: { name: '', slug: '' } });
    this.props.history.push(generatePath('properties', {}));
  };

  handleSelectSearch = value => {
    this.setState({ searchType: value, source: [], value: '', lastSearch: { name: '', slug: '' } });
  };

  render() {
    return (
      <Input.Group compact className="autocomplete">
        <Select
          defaultValue={this.state.searchType}
          className="autocomplete__select"
          onSelect={this.handleSelectSearch}
        >
          <Select.Option value="propertySlug">
            {this.props.t('cms.reconciliation.lead_generation.filter.property')}
          </Select.Option>
          <Select.Option value="landlordId">
            {this.props.t('cms.reconciliation.lead_generation.filter.landlord')}
          </Select.Option>
          <Select.Option value="bookingId">
            {this.props.t('cms.search.title.booking')}
          </Select.Option>
          <Select.Option value="citySlugAndCountrySlug">
            {this.props.t('cms.search_select.option.city_country')}
          </Select.Option>
        </Select>
        <AutoComplete
          allowClear={false}
          className="autocomplete__wrapper"
          dropdownClassName="autocomplete__dropdown"
          dataSource={this.getChildren()}
          placeholder={this.searchPlaceholder[this.state.searchType]}
          optionLabelProp="value"
          onSearch={this.handleSearch}
          onSelect={this.handleSelect}
          onChange={this.handleChange}
          value={this.state.value}
        >
          <Input
            prefix={<SearchOutlined className="autocomplete__icon" />}
            value={this.state.value}
            className="autocomplete__input"
          />
        </AutoComplete>
      </Input.Group>
    );
  }
}

GlobalSearch.propTypes = {
  t: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  search: PropTypes.string,
};

GlobalSearch.defaultProps = {
  t: () => {},
  search: '',
};
